import { useEffect, useState, useCallback } from 'react';
import {
  Plus, Search, Filter, X, ChevronDown, ChevronRight, ChevronLeft, Loader2, MoreVertical,
  Phone, Mail, MessageCircle, Calendar as CalendarIcon, FileText, Pencil, Trash2,
  RefreshCw, CheckCircle, AlertCircle, ArrowRight, LayoutGrid, LayoutList,
  Building2, User as UserIcon, Tag, Copy, ExternalLink, Crown, Check, MapPin, Info, History,
} from 'lucide-react';
import { SuperAdminLayout } from '@/components/super-admin/SuperAdminLayout';
import { DemoBookingCard, DemoBooking } from '@/components/super-admin/DemoBookingCard';
import { saApi } from '@/contexts/SuperAdminAuthContext';
import { DateInput } from '@/components/ui/DateInput';
import { toast } from '@/components/ui/toast';
import { ConfirmModal } from '@/components/ConfirmModal';
import { TableRowSkeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/EmptyState';
import { extractErrorMessage } from '@/lib/errorMessage';
import { formatDate, formatDateTime, formatTime } from '@/lib/dateFormat';
import { useSort, SortTh, TableHeadRow } from '@/components/ui/sortable-table';
import { useSidebarEdge } from '@/lib/useSidebarEdge';
import { DateTimeInput } from '@/components/ui/DateTimeInput';
import { SelectField } from '@/components/ui/SelectField';

// ────────────────────────────────────────────────────────────────────────────
// Super-admin sales pipeline page. Single-route at /super-admin/prospects.
// List + kanban toggle, right-side drawer for detail with timeline + actions.
//
// All requests go through saApi (super-admin axios instance with the
// saAccessToken header), so the SuperAdminGuard on the backend gates this
// without any additional setup.
// ────────────────────────────────────────────────────────────────────────────

const STATUSES = [
  { id: 'new',             label: 'New',             color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { id: 'contacted',       label: 'Contacted',       color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { id: 'demo_scheduled',  label: 'Demo Scheduled',  color: 'bg-violet-50 text-violet-700 border-violet-200' },
  { id: 'demo_done',       label: 'Demo Done',       color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  { id: 'proposal_sent',   label: 'Proposal Sent',   color: 'bg-purple-50 text-purple-700 border-purple-200' },
  { id: 'negotiating',     label: 'Negotiating',     color: 'bg-orange-50 text-orange-700 border-orange-200' },
  { id: 'won',             label: 'Won',             color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { id: 'lost',            label: 'Lost',            color: 'bg-slate-100 text-slate-500 border-slate-200' },
] as const;

// Human-readable label for a status id (falls back to the raw id). Used so the
// timeline shows "Demo Done" instead of the backend value "demo_done".
const statusLabel = (id: string) => STATUSES.find(s => s.id === id)?.label ?? id;

const SOURCES = ['website', 'referral', 'cold_outreach', 'demo_request', 'webinar', 'partner', 'other'];

interface Prospect {
  id: string;
  companyName: string;
  contactName: string;
  email: string | null;
  phone: string | null;
  source: string | null;
  status: string;
  estimatedValue: number | null;
  expectedCloseDate: string | null;
  lastContactAt: string | null;
  demoScheduledAt: string | null;
  notes: string | null;
  hashtags: string[];
  // Campaign attribution (populated for ad-sourced leads)
  campaignName?: string | null;
  adName?: string | null;
  adId?: string | null;
  campaignId?: string | null;
  formId?: string | null;
  fbclid?: string | null;
  leadgenId?: string | null;
  assignedToId: string | null;
  assignedTo: { id: string; name: string; email: string } | null;
  countryId?: string | null;
  // Latest remark-bearing activity (list endpoint only, at most one).
  activities?: { id: string; actionType: string; payload: any; actorName: string | null; createdAt: string }[];
  convertedTenantId: string | null;
  convertedTenant: { id: string; name: string; slug?: string } | null;
  convertedAt: string | null;
  lossReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export function SuperAdminProspectsPage() {
  const [view, setView] = useState<'list' | 'kanban'>('list');
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', status: '', source: '' });
  // Server-side paging (list view). Kanban loads the max page (100) instead.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [selected, setSelected] = useState<Prospect | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const fetchProspects = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = view === 'kanban' ? { page: 1, limit: 100 } : { page, limit: pageSize };
      if (filters.search) params.search = filters.search;
      if (filters.status) params.status = filters.status;
      if (filters.source) params.source = filters.source;
      const [list, counts] = await Promise.all([
        saApi.get('/super-admin/prospects', { params }),
        saApi.get('/super-admin/prospects/status-counts'),
      ]);
      setProspects(list.data.prospects);
      setTotal(Number(list.data.total ?? list.data.prospects?.length ?? 0));
      setTotalPages(Math.max(1, Number(list.data.totalPages ?? 1)));
      setStatusCounts(counts.data);
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Failed to load prospects'));
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize, view]);

  useEffect(() => { fetchProspects(); }, [fetchProspects]);
  // Any filter / page-size change goes back to the first page.
  useEffect(() => { setPage(1); }, [filters, pageSize]);

  const totalActive = STATUSES.filter(s => s.id !== 'lost' && s.id !== 'won')
    .reduce((sum, s) => sum + (statusCounts[s.id] ?? 0), 0);
  const wonCount = statusCounts['won'] ?? 0;
  const lostCount = statusCounts['lost'] ?? 0;

  const hasFilters = !!(filters.search || filters.status || filters.source);

  return (
    <SuperAdminLayout title="Prospects">
      {/* ── Stats row ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Active pipeline" value={totalActive} tone="indigo" />
        <StatCard label="Won" value={wonCount} tone="emerald" />
        <StatCard label="Lost" value={lostCount} tone="rose" />
        <StatCard
          label="Conversion rate"
          value={
            wonCount + lostCount > 0
              ? `${Math.round((wonCount / (wonCount + lostCount)) * 100)}%`
              : '—'
          }
          tone="amber"
        />
      </div>

      {/* ── Toolbar ───────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 mb-5">
        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 p-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="search"
              placeholder="Search company, contact, email…"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-full pl-10 pr-3 h-10 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
            />
          </div>
          <button
            onClick={() => setShowFilters(v => !v)}
            className={`inline-flex items-center gap-1.5 px-3 h-10 rounded-lg border text-sm font-medium ${
              hasFilters ? 'border-[#6366F1] text-[#6366F1]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Filter className="h-3.5 w-3.5" /> Filters
          </button>
          <div className="inline-flex border border-slate-200 rounded-lg p-0.5 bg-slate-50">
            <button
              onClick={() => setView('list')}
              className={`px-3 h-9 rounded text-xs font-semibold inline-flex items-center gap-1.5 ${
                view === 'list' ? 'bg-white shadow text-[#6366F1]' : 'text-slate-500'
              }`}
            >
              <LayoutList className="h-3.5 w-3.5" /> List
            </button>
            <button
              onClick={() => setView('kanban')}
              className={`px-3 h-9 rounded text-xs font-semibold inline-flex items-center gap-1.5 ${
                view === 'kanban' ? 'bg-white shadow text-[#6366F1]' : 'text-slate-500'
              }`}
            >
              <LayoutGrid className="h-3.5 w-3.5" /> Kanban
            </button>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold"
          >
            <Plus className="h-4 w-4" /> New Prospect
          </button>
        </div>

        {showFilters && (
          <div className="border-t border-slate-100 p-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <SelectField
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm"
            >
              <option value="">All statuses</option>
              {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </SelectField>
            <SelectField
              value={filters.source}
              onChange={(e) => setFilters({ ...filters, source: e.target.value })}
              className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm"
            >
              <option value="">All sources</option>
              {SOURCES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </SelectField>
            {hasFilters && (
              <button
                onClick={() => setFilters({ search: '', status: '', source: '' })}
                className="text-xs text-[#6366F1] inline-flex items-center gap-1 self-center"
              >
                <X className="h-3 w-3" /> Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── List or Kanban ───────────────────────────────────────────── */}
      {loading ? (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full"><tbody>
            {Array.from({ length: 6 }).map((_, i) => <TableRowSkeleton key={i} columns={9} />)}
          </tbody></table>
        </div>
      ) : prospects.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200">
          <EmptyState
            icon={Building2}
            title={hasFilters ? 'No prospects match your filters' : 'No prospects yet'}
            description={
              hasFilters
                ? 'Try widening your filters or clearing them.'
                : 'Add your first prospect to start tracking your sales pipeline.'
            }
            action={
              hasFilters ? (
                <button
                  onClick={() => setFilters({ search: '', status: '', source: '' })}
                  className="px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm"
                >Clear filters</button>
              ) : (
                <button
                  onClick={() => setShowCreate(true)}
                  className="px-4 py-2 rounded-lg bg-[#6366F1] text-white hover:bg-[#4F46E5] text-sm inline-flex items-center gap-1.5"
                >
                  <Plus className="h-3.5 w-3.5" /> Add prospect
                </button>
              )
            }
          />
        </div>
      ) : view === 'list' ? (
        <>
          <ListView prospects={prospects} onSelect={setSelected} onRefresh={fetchProspects} startIndex={(page - 1) * pageSize} />
          <PaginationBar
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={setPageSize}
          />
        </>
      ) : (
        <KanbanView prospects={prospects} onSelect={setSelected} />
      )}

      {/* ── Drawer + modals ─────────────────────────────────────────── */}
      {selected && (
        <ProspectDetailDrawer
          prospect={selected}
          onClose={() => setSelected(null)}
          onChange={() => fetchProspects()}
        />
      )}
      {showCreate && (
        <ProspectFormDrawer
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); fetchProspects(); }}
        />
      )}
    </SuperAdminLayout>
  );
}

// ── Stat tile ──────────────────────────────────────────────────────────────

// Paging footer — mirrors the vendor Leads list (rows per page, First / ‹ /
// 5 page numbers / › / Last).
function PaginationBar({ page, totalPages, total, pageSize, onPage, onPageSize }: {
  page: number; totalPages: number; total: number; pageSize: number;
  onPage: (p: number) => void; onPageSize: (n: number) => void;
}) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
  const pages = Array.from({ length: Math.min(5, totalPages) }, (_, i) => start + i);
  const btn = 'h-8 min-w-8 px-2 rounded-lg text-xs border border-slate-200 bg-white text-slate-600 hover:border-[#6366F1] hover:text-[#6366F1] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-slate-200 disabled:hover:text-slate-600';
  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className="flex items-center gap-3 text-xs text-slate-500">
        <span>Showing {from}–{to} of {total}</span>
        <label className="flex items-center gap-1.5">
          Rows per page
          <SelectField
            value={pageSize}
            onChange={e => onPageSize(Number(e.target.value))}
            className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
          >
            {[25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
          </SelectField>
        </label>
      </div>
      <div className="flex items-center gap-1">
        <span className="mr-2 text-xs text-slate-500">Page {page} of {totalPages}</span>
        <button className={btn} disabled={page <= 1} onClick={() => onPage(1)}>First</button>
        <button className={btn} disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous page"><ChevronLeft className="h-4 w-4 mx-auto" /></button>
        {pages.map(p => (
          <button
            key={p}
            onClick={() => onPage(p)}
            className={p === page ? 'h-8 min-w-8 px-2 rounded-lg text-xs bg-[#6366F1] text-white' : btn}
          >
            {p}
          </button>
        ))}
        <button className={btn} disabled={page >= totalPages} onClick={() => onPage(page + 1)} aria-label="Next page"><ChevronRight className="h-4 w-4 mx-auto" /></button>
        <button className={btn} disabled={page >= totalPages} onClick={() => onPage(totalPages)}>Last</button>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone }: { label: string; value: string | number; tone: 'indigo' | 'emerald' | 'rose' | 'amber' }) {
  const map = {
    indigo:  'from-indigo-500 to-purple-600',
    emerald: 'from-emerald-500 to-green-600',
    rose:    'from-rose-500 to-red-600',
    amber:   'from-amber-500 to-orange-600',
  };
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4">
      <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
      <p className={`text-2xl font-bold mt-1 bg-gradient-to-r bg-clip-text text-transparent ${map[tone]}`}>
        {value}
      </p>
    </div>
  );
}

// ── List view ──────────────────────────────────────────────────────────────

// Remark text carried by a timeline activity (note / status remark / logged call…).
const activityRemark = (a: { actionType: string; payload: any }): string => {
  const p = a.payload ?? {};
  if (a.actionType === 'note_added') return p.body ?? '';
  if (a.actionType === 'status_changed') return p.note ?? '';
  return p.summary ?? '';
};

function ListView({ prospects, onSelect, onRefresh, startIndex = 0 }: { prospects: Prospect[]; onSelect: (p: Prospect) => void; onRefresh: () => void; startIndex?: number }) {
  const { sorted, sort, onSort } = useSort(prospects);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [expandedRemarks, setExpandedRemarks] = useState<Set<string>>(new Set());
  const [countryNames, setCountryNames] = useState<Map<string, string>>(new Map());

  // Country id → name for the Location column (geo master, cached per page).
  useEffect(() => {
    saApi.get('/geo/countries')
      .then(({ data }) => setCountryNames(new Map((Array.isArray(data) ? data : []).map((c: any) => [c.id, c.name]))))
      .catch(() => {});
  }, []);

  const toggleRemark = (id: string) =>
    setExpandedRemarks(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // Latest activity under the status: "who · when" + remark with more/less.
  const renderLatest = (p: Prospect) => {
    const a = p.activities?.[0];
    if (!a) return null;
    const remark = activityRemark(a);
    const long = remark.length > 90;
    const open = expandedRemarks.has(p.id);
    return (
      <div className="mt-1.5">
        <p className="text-xs text-slate-500">{a.actorName ?? 'System'} · {formatDateTime(a.createdAt)}</p>
        {remark && (
          <p className="mt-0.5 text-xs text-slate-700 whitespace-pre-line break-words">
            {long && !open ? `${remark.slice(0, 90).trimEnd()}…` : remark}
            {long && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggleRemark(p.id); }}
                className="ml-1 font-medium text-[#6366F1] hover:underline"
              >
                {open ? 'less' : 'more'}
              </button>
            )}
          </p>
        )}
      </div>
    );
  };
  const sourceLabel = (src: string | null) => (src ? src.replace(/_/g, ' ') : '—');

  // Drop selections for rows no longer in the list (after a refresh/filter).
  useEffect(() => {
    setSelectedIds(prev => {
      const ids = new Set(sorted.map(p => p.id));
      const next = new Set(Array.from(prev).filter(id => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [sorted]);

  const allSelected = sorted.length > 0 && sorted.every(p => selectedIds.has(p.id));
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleOne = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(sorted.map(p => p.id)));
  };

  const bulkDelete = async () => {
    setBulkBusy(true);
    try {
      const { data } = await saApi.post('/super-admin/prospects/bulk-delete', {
        ids: Array.from(selectedIds),
      });
      const deleted = data?.deleted ?? 0;
      const skipped = data?.skipped ?? 0;
      toast('success', `${deleted} deleted${skipped ? `, ${skipped} skipped (converted)` : ''}`);
      setSelectedIds(new Set());
      setConfirmBulk(false);
      onRefresh();
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Failed to delete prospects'));
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Bulk-action bar — appears once one or more rows are selected. */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 border-b border-slate-100 bg-indigo-50/60 px-4 py-2.5">
          <span className="text-sm font-medium text-slate-700">{selectedIds.size} selected</span>
          <button
            onClick={() => setConfirmBulk(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1.5"
          >
            <Trash2 className="h-3.5 w-3.5" /> Delete selected
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-xs font-medium text-slate-600 hover:text-slate-900"
          >
            Clear
          </button>
        </div>
      )}
      {/* Mobile cards — same info as the table rows */}
      <div className="md:hidden divide-y divide-slate-100">
        {sorted.map((p, idx) => {
          const status = STATUSES.find(s => s.id === p.status);
          const checked = selectedIds.has(p.id);
          return (
            <div key={p.id} className={`px-4 py-3 ${checked ? 'bg-indigo-50/50' : ''}`} onClick={() => onSelect(p)}>
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleOne(p.id)}
                  onClick={e => e.stopPropagation()}
                  className="h-4 w-4 mt-1 cursor-pointer accent-[#6366F1]"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm">
                    <span className="text-xs text-slate-400 mr-1.5">{startIndex + idx + 1}.</span>
                    <span className="font-medium text-[#6366F1]">{p.companyName}</span>
                    {p.convertedTenant && <CheckCircle className="inline h-3.5 w-3.5 text-emerald-500 ml-1.5" />}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5 capitalize">
                    {[sourceLabel(p.source), formatDateTime(p.createdAt)].join(' · ')}
                  </p>
                  <p className="mt-2 font-medium text-slate-800 text-sm truncate">{p.contactName}</p>
                  {p.phone && <p className="text-xs text-slate-600 truncate mt-0.5">{p.phone}</p>}
                  {p.email && <p className="text-xs text-slate-500 truncate mt-0.5">{p.email}</p>}
                  <span className={`mt-2 inline-block rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${status?.color}`}>
                    {status?.label ?? p.status}
                  </span>
                  {renderLatest(p)}
                  <div className="mt-2 flex flex-wrap items-center gap-x-2 text-xs">
                    <span className="text-slate-700">{(p.countryId && countryNames.get(p.countryId)) || '—'}</span>
                    <span className="text-slate-300">•</span>
                    <span className={p.assignedTo?.name ? 'text-slate-700' : 'text-slate-400'}>{p.assignedTo?.name || 'Unassigned'}</span>
                    {p.estimatedValue ? (
                      <>
                        <span className="text-slate-300">•</span>
                        <span className="text-slate-700">₹{p.estimatedValue.toLocaleString('en-IN')}</span>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <TableHeadRow>
              <SortTh
                sortable={false}
                align="center"
                className="w-10"
                label={
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={el => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleAll}
                    className="h-4 w-4 cursor-pointer accent-white"
                    title="Select all"
                  />
                }
              />
              <SortTh label="S.No" sortable={false} className="w-12" />
              {/* Merged like the vendor leads list: Prospect = company + source
                  + created, Contact = name + phone + email. */}
              <SortTh label="Prospect" sortKey="createdAt" sort={sort} onSort={onSort} />
              <SortTh label="Contact" sortKey="contactName" sort={sort} onSort={onSort} />
              <SortTh label="Status" sortKey="status" sort={sort} onSort={onSort} />
              <SortTh label="Location" sortable={false} />
              <SortTh label="Assignee" sortable={false} />
              <SortTh label="Value" sortKey="estimatedValue" sort={sort} onSort={onSort} />
            </TableHeadRow>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {sorted.map((p, idx) => {
              const status = STATUSES.find(s => s.id === p.status);
              const checked = selectedIds.has(p.id);
              return (
                <tr
                  key={p.id}
                  onClick={() => onSelect(p)}
                  className={`cursor-pointer align-top [&>td]:align-top transition-colors ${checked ? 'bg-indigo-50/50' : 'hover:bg-slate-50/80'}`}
                >
                  <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleOne(p.id)}
                      className="h-4 w-4 cursor-pointer accent-[#6366F1]"
                    />
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400 tabular-nums">{startIndex + idx + 1}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-[#6366F1] hover:underline">
                      {p.companyName}
                      {p.convertedTenant && (
                        <CheckCircle className="inline h-3.5 w-3.5 text-emerald-500 ml-1.5" />
                      )}
                    </p>
                    <p className="text-xs text-slate-600 mt-0.5 capitalize">{sourceLabel(p.source)}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{formatDateTime(p.createdAt)}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-800">{p.contactName}</p>
                    {p.phone && <p className="text-xs text-slate-600 mt-0.5">{p.phone}</p>}
                    {p.email && <p className="text-xs text-slate-500 mt-0.5 break-all">{p.email}</p>}
                  </td>
                  <td className="px-4 py-3 max-w-[280px]">
                    <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${status?.color}`}>
                      {status?.label ?? p.status}
                    </span>
                    {renderLatest(p)}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-800">
                    {(p.countryId && countryNames.get(p.countryId)) || '—'}
                  </td>
                  <td className={`px-4 py-3 text-sm ${p.assignedTo?.name ? 'text-slate-800' : 'text-slate-400'}`}>
                    {p.assignedTo?.name || 'Unassigned'}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {p.estimatedValue ? `₹${p.estimatedValue.toLocaleString('en-IN')}` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        isOpen={confirmBulk}
        title={`Delete ${selectedIds.size} prospect${selectedIds.size === 1 ? '' : 's'}?`}
        message="Selected prospects will be permanently deleted. Any that were converted to a tenant are skipped. This cannot be undone."
        confirmText="Delete"
        onConfirm={bulkDelete}
        onCancel={() => (bulkBusy ? null : setConfirmBulk(false))}
        isLoading={bulkBusy}
      />
    </div>
  );
}

// ── Kanban view ────────────────────────────────────────────────────────────

function KanbanView({ prospects, onSelect }: { prospects: Prospect[]; onSelect: (p: Prospect) => void }) {
  const grouped: Record<string, Prospect[]> = {};
  for (const s of STATUSES) grouped[s.id] = [];
  for (const p of prospects) (grouped[p.status] ?? grouped['new']).push(p);

  return (
    <div className="overflow-x-auto pb-4">
      <div className="flex gap-3 min-w-max">
        {STATUSES.map(s => (
          <div key={s.id} className="w-72 flex-shrink-0">
            <div className={`rounded-t-lg px-3 py-2 border ${s.color} text-xs font-semibold flex items-center justify-between`}>
              <span>{s.label}</span>
              <span className="bg-white/60 rounded-full px-1.5 py-0.5 text-[10px]">
                {grouped[s.id].length}
              </span>
            </div>
            <div className="bg-slate-50/50 border-x border-b border-slate-200 rounded-b-lg p-2 space-y-2 min-h-[120px]">
              {grouped[s.id].length === 0 ? (
                <p className="text-[11px] text-slate-400 text-center py-6">No prospects</p>
              ) : grouped[s.id].map(p => (
                <button
                  key={p.id}
                  onClick={() => onSelect(p)}
                  className="w-full text-left bg-white rounded-lg border border-slate-200 p-3 hover:border-[#6366F1] hover:shadow-sm transition-all"
                >
                  <p className="font-semibold text-slate-800 text-sm truncate">{p.companyName}</p>
                  <p className="text-xs text-slate-500 truncate">{p.contactName}</p>
                  {p.estimatedValue && (
                    <p className="text-xs text-emerald-600 font-semibold mt-1">
                      ₹{p.estimatedValue.toLocaleString('en-IN')}
                    </p>
                  )}
                  {p.assignedTo && (
                    <p className="text-[10px] text-slate-400 mt-1 truncate">@ {p.assignedTo.name}</p>
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Create / Edit drawer ───────────────────────────────────────────────────

function ProspectFormDrawer({
  prospect,
  onClose,
  onSaved,
}: {
  prospect?: Prospect;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!prospect;
  const [form, setForm] = useState({
    companyName: prospect?.companyName ?? '',
    contactName: prospect?.contactName ?? '',
    email: prospect?.email ?? '',
    phone: prospect?.phone ?? '',
    source: prospect?.source ?? '',
    status: prospect?.status ?? 'new',
    estimatedValue: prospect?.estimatedValue ?? '',
    expectedCloseDate: prospect?.expectedCloseDate?.slice(0, 10) ?? '',
    assignedToId: prospect?.assignedToId ?? '',
    notes: prospect?.notes ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [admins, setAdmins] = useState<{ id: string; name: string; email: string }[]>([]);

  useEffect(() => {
    saApi.get('/super-admin/users')
      .then(({ data }) => setAdmins(Array.isArray(data) ? data : (data?.users ?? [])))
      .catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.companyName.trim() || !form.contactName.trim() || !form.email.trim()) {
      toast('error', 'Company, contact, and email are required');
      return;
    }
    setSaving(true);
    try {
      const payload: any = { ...form };
      payload.estimatedValue = form.estimatedValue ? Number(form.estimatedValue) : null;
      payload.assignedToId = form.assignedToId || null;
      if (!form.expectedCloseDate) delete payload.expectedCloseDate;
      if (isEdit) await saApi.patch(`/super-admin/prospects/${prospect!.id}`, payload);
      else        await saApi.post('/super-admin/prospects', payload);
      toast('success', isEdit ? 'Prospect updated' : 'Prospect created');
      onSaved();
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-md bg-white shadow-2xl flex flex-col h-full overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-900">{isEdit ? 'Edit Prospect' : 'New Prospect'}</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded">
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <FormField label="Company name *">
            <input value={form.companyName} onChange={e => setForm({ ...form, companyName: e.target.value })}
              placeholder="Acme Coaching Pvt Ltd" className={INPUT_CLS} />
          </FormField>
          <FormField label="Contact person *">
            <input value={form.contactName} onChange={e => setForm({ ...form, contactName: e.target.value })}
              placeholder="Ramesh Sharma" className={INPUT_CLS} />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Email *">
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="ramesh@acme.com" className={INPUT_CLS} />
            </FormField>
            <FormField label="Phone">
              <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                placeholder="+91 9876543210" className={INPUT_CLS} />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Status">
              <SelectField value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                className={INPUT_CLS}>
                {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </SelectField>
            </FormField>
            <FormField label="Source">
              <SelectField value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}
                className={INPUT_CLS}>
                <option value="">—</option>
                {SOURCES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </SelectField>
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Estimated value (₹)">
              <input type="number" value={form.estimatedValue} onChange={e => setForm({ ...form, estimatedValue: e.target.value })}
                placeholder="50000" className={INPUT_CLS} />
            </FormField>
            <FormField label="Expected close date">
              <DateTimeInput mode="date" value={form.expectedCloseDate} onChange={v => setForm({ ...form, expectedCloseDate: v })} />
            </FormField>
          </div>
          <FormField label="Assigned to">
            <SelectField value={form.assignedToId} onChange={e => setForm({ ...form, assignedToId: e.target.value })}
              className={INPUT_CLS}>
              <option value="">Unassigned</option>
              {admins.map(a => <option key={a.id} value={a.id}>{a.name || a.email}</option>)}
            </SelectField>
          </FormField>
          <FormField label="Notes">
            <textarea rows={4} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
              placeholder="Anything important to remember…" className={INPUT_CLS + ' resize-none'} />
          </FormField>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm">Cancel</button>
          <button onClick={submit} disabled={saving}
            className="px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold inline-flex items-center gap-2">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {isEdit ? 'Save changes' : 'Create prospect'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Detail drawer ──────────────────────────────────────────────────────────

function ProspectDetailDrawer({
  prospect,
  onClose,
  onChange,
}: {
  prospect: Prospect;
  onClose: () => void;
  onChange: () => void;
}) {
  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showBookDemo, setShowBookDemo] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [logBusy, setLogBusy] = useState<'note' | 'call' | 'email' | 'whatsapp' | null>(null);
  const [ctaBusy, setCtaBusy] = useState(false);
  const [tab, setTab] = useState<'details' | 'timeline' | 'demo'>('details');
  const [bookings, setBookings] = useState<DemoBooking[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [countryName, setCountryName] = useState<string | null>(null);
  // Drawer spans from the right edge up to the sidebar (like the leads drawer).
  const sidebarEdge = useSidebarEdge();

  // Escape closes the drawer (modals on top handle their own Escape).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await saApi.get(`/super-admin/prospects/${prospect.id}`);
      setDetail(data);
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Failed to load prospect'));
    } finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, [prospect.id]);

  const loadBookings = async () => {
    setBookingsLoading(true);
    try {
      const { data } = await saApi.get(`/super-admin/prospects/${prospect.id}/bookings`);
      setBookings(Array.isArray(data) ? data : []);
    } catch { setBookings([]); }
    finally { setBookingsLoading(false); }
  };
  useEffect(() => { loadBookings(); }, [prospect.id]);

  // Status changes go through a confirm step with an optional remark instead of
  // firing on the pill click. pendingStatus holds the clicked target.
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const [statusRemark, setStatusRemark] = useState('');
  const [statusBusy, setStatusBusy] = useState(false);

  const confirmStatus = async () => {
    if (!pendingStatus) return;
    setStatusBusy(true);
    try {
      await saApi.patch(`/super-admin/prospects/${prospect.id}`, {
        status: pendingStatus,
        statusNote: statusRemark.trim() || undefined,
      });
      toast('success', 'Status updated');
      setPendingStatus(null);
      setStatusRemark('');
      fetch();
      onChange();
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Failed to update status'));
    } finally {
      setStatusBusy(false);
    }
  };

  const log = async (type: 'note' | 'call' | 'email' | 'whatsapp', body?: string) => {
    setLogBusy(type);
    try {
      const path =
        type === 'note' ? '/note' :
        type === 'call' ? '/log-call' :
        type === 'email' ? '/log-email' :
        '/log-whatsapp';
      const payload = type === 'note' ? { body } : { summary: body };
      await saApi.post(`/super-admin/prospects/${prospect.id}${path}`, payload);
      setNoteText('');
      fetch();
      toast('success', 'Logged');
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Failed to log'));
    } finally { setLogBusy(null); }
  };

  const remove = async () => {
    try {
      await saApi.delete(`/super-admin/prospects/${prospect.id}`);
      toast('success', 'Prospect deleted');
      onChange();
      onClose();
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Failed to delete'));
    }
  };

  // Option C — enable/disable the vendor's self-serve "Upgrade Plan" CTA
  // straight from the prospect. Only works once the prospect maps to a tenant.
  const toggleUpgradeCta = async () => {
    setCtaBusy(true);
    try {
      const next = !detail?.upgradeCtaEnabled;
      await saApi.patch(`/super-admin/prospects/${prospect.id}/upgrade-cta`, { enabled: next });
      toast('success', next ? 'Upgrade Plan enabled for this vendor' : 'Upgrade Plan disabled');
      fetch();
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Failed to update the upgrade option'));
    } finally {
      setCtaBusy(false);
    }
  };

  // Prefer the freshly-fetched detail (updates after Book Demo / status change)
  // over the list-row prop, which is stale once the drawer is open.
  const p = (detail ?? prospect) as typeof prospect;
  const status = STATUSES.find(s => s.id === p.status);
  const isConverted = !!detail?.convertedTenantId;
  // A tenant already exists for this email (e.g. self-signup) but this prospect
  // was never formally converted — so we surface it instead of offering Convert.
  const existingTenant = detail?.existingTenant as { id: string; name: string; slug: string } | null | undefined;
  const alreadyTenant = !!existingTenant && !isConverted;

  // Country name for the info strip (geo master).
  const countryId = (detail?.countryId ?? (prospect as any).countryId) as string | null | undefined;
  useEffect(() => {
    if (!countryId) { setCountryName(null); return; }
    saApi.get('/geo/countries')
      .then(({ data }) => setCountryName((Array.isArray(data) ? data : []).find((c: any) => c.id === countryId)?.name ?? null))
      .catch(() => {});
  }, [countryId]);

  // Header / info-strip derived values (mirrors the vendor Lead Detail).
  const initials = String(p.companyName || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('') || '?';
  const phoneDigits = (p.phone || '').replace(/[^\d+]/g, '');
  const activities: any[] = Array.isArray(detail?.activities) ? detail.activities : [];
  const lastStatusChange = activities
    .filter(a => a.actionType === 'status_changed' && a.payload?.from)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
  const prevStatus: string | undefined = lastStatusChange?.payload?.from;
  const statusTrail: string[] = [
    ...(prevStatus && prevStatus !== p.status ? [statusLabel(prevStatus)] : []),
    statusLabel(p.status),
  ];
  const timeAgo = (iso: string) => {
    const sec = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (sec < 60) return 'just now';
    if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)} h ago`;
    const d = Math.floor(sec / 86400);
    return d < 30 ? `${d} d ago` : formatDate(iso);
  };
  const actionBtn = 'inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 sm:px-3.5 text-sm font-medium text-slate-700 hover:bg-slate-50';

  return (
    <div
      className="fixed inset-y-0 right-0 z-50 flex flex-col bg-[#F8F9FA] shadow-2xl border-l border-slate-200 animate-in slide-in-from-right duration-300"
      style={{ left: sidebarEdge }}
    >
      <div className="flex h-12 sm:h-14 flex-shrink-0 items-center justify-end border-b border-slate-100 bg-white px-3 sm:px-6">
        <button onClick={onClose} aria-label="Close" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-6 space-y-4">
        {/* Header card — avatar, name, badges, quick actions, status */}
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-r from-white via-white to-indigo-50/70 p-4 sm:p-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-3 min-w-0">
              <div className="flex h-11 w-11 sm:h-14 sm:w-14 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] text-base sm:text-lg font-bold text-white">
                {initials}
              </div>
              <div className="min-w-0">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 break-words">{p.companyName}</h2>
                <p className="text-sm text-slate-600">{p.contactName}</p>
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  {p.source && (
                    <span className="text-xs text-slate-500">via <span className="font-semibold text-slate-700 capitalize">{p.source.replace(/_/g, ' ')}</span></span>
                  )}
                  {p.campaignName && (
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-indigo-50 text-indigo-600 border border-indigo-100 break-all">
                      {p.campaignName}
                    </span>
                  )}
                  <span className="text-xs text-slate-400">{formatDateTime(p.createdAt)}</span>
                  {p.estimatedValue ? (
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-700">
                      ₹{p.estimatedValue.toLocaleString('en-IN')}
                    </span>
                  ) : null}
                  {isConverted && (
                    <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-medium">
                      <CheckCircle className="h-3 w-3" /> Converted
                    </span>
                  )}
                  {alreadyTenant && existingTenant && (
                    <a
                      href={`/super-admin/organizations?search=${encodeURIComponent(existingTenant.name)}`}
                      className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-medium hover:underline"
                      title="This email already has a BrixLead tenant"
                    >
                      <CheckCircle className="h-3 w-3" /> Already a tenant: {existingTenant.name}
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Quick actions */}
            <div className="flex flex-wrap gap-2 lg:justify-end">
              {p.phone && (
                <a href={`tel:${phoneDigits}`} className={actionBtn}><Phone className="h-4 w-4" /> <span className="hidden sm:inline">Call</span></a>
              )}
              {p.phone && (
                <a href={`https://wa.me/${phoneDigits.replace('+', '')}`} target="_blank" rel="noopener noreferrer" className={actionBtn}>
                  <MessageCircle className="h-4 w-4" /> <span className="hidden sm:inline">WhatsApp</span>
                </a>
              )}
              {p.email && (
                <a href={`mailto:${p.email}`} className={actionBtn}><Mail className="h-4 w-4" /> <span className="hidden sm:inline">Email</span></a>
              )}
              <button onClick={() => setShowEdit(true)} className={actionBtn}>
                <Pencil className="h-4 w-4" /> <span className="hidden sm:inline">Edit</span>
              </button>
              {!isConverted && (
                <button onClick={() => setShowDelete(true)} title="Delete prospect" className="inline-flex h-10 items-center rounded-lg border border-red-200 bg-white px-3 text-red-600 hover:bg-red-50">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Pipeline actions */}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => setShowBookDemo(true)}
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-xs font-semibold"
            >
              <CalendarIcon className="h-3.5 w-3.5" /> Book Demo
            </button>
            {!isConverted && !alreadyTenant && (
              <button
                onClick={() => setShowConvert(true)}
                className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
              >
                <ArrowRight className="h-3.5 w-3.5" /> Convert to tenant
              </button>
            )}
            {/* Option C — only a prospect that maps to a tenant can have its
                self-serve Upgrade Plan CTA toggled. */}
            {detail?.linkedTenantId && (
              <button
                onClick={toggleUpgradeCta}
                disabled={ctaBusy}
                title={detail?.upgradeCtaEnabled
                  ? 'Vendor can see the Upgrade Plan button. Click to hide it.'
                  : "Show the Upgrade Plan button in this vendor's Billing page."}
                className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-xs font-semibold disabled:opacity-50 ${
                  detail?.upgradeCtaEnabled
                    ? 'bg-amber-500 hover:bg-amber-600 text-white'
                    : 'border border-amber-300 text-amber-700 hover:bg-amber-50'
                }`}
              >
                {ctaBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Crown className="h-3.5 w-3.5" />}
                {detail?.upgradeCtaEnabled ? 'Upgrade Plan: On' : 'Enable Upgrade Plan'}
              </button>
            )}
          </div>

          {/* Status trail — previous status (done) → current status */}
          <div className="no-scrollbar mt-4 flex items-center overflow-x-auto">
            {statusTrail.map((st, i) => {
              const current = i === statusTrail.length - 1;
              return (
                <span
                  key={`${st}-${i}`}
                  className={`inline-flex h-9 flex-shrink-0 whitespace-nowrap items-center gap-1.5 pl-5 pr-6 text-xs font-semibold ${i > 0 ? '-ml-2' : ''} ${
                    current ? 'bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white' : 'bg-emerald-50 text-emerald-700'
                  }`}
                  style={{
                    clipPath: i === 0
                      ? 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%)'
                      : 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%)',
                  }}
                >
                  {!current && <Check className="h-3.5 w-3.5" />}
                  {st}
                </span>
              );
            })}
          </div>

          {/* Move to another status — confirm (with optional remark) → update */}
          <div className="mt-3">
            <p className="text-xs text-slate-500 mb-1.5">Move to</p>
            <div className="no-scrollbar flex gap-1 overflow-x-auto">
              {STATUSES.map(s => (
                <button
                  key={s.id}
                  onClick={() => { if (p.status !== s.id) { setStatusRemark(''); setPendingStatus(s.id); } }}
                  className={`inline-flex flex-shrink-0 items-center px-2.5 h-7 rounded-full border text-[11px] font-medium whitespace-nowrap ${
                    (pendingStatus ?? p.status) === s.id ? s.color : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            {pendingStatus && (
              <div className="mt-3 rounded-lg border border-indigo-100 bg-indigo-50/50 p-3">
                <p className="text-xs text-slate-700 mb-2">
                  Change status to <b>{STATUSES.find(s => s.id === pendingStatus)?.label ?? pendingStatus}</b>?
                </p>
                <textarea
                  value={statusRemark}
                  onChange={e => setStatusRemark(e.target.value)}
                  rows={2}
                  placeholder="Add a remark (optional) — shown on the timeline"
                  className="w-full text-xs rounded-lg border border-slate-200 bg-white px-2.5 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-[#6366F1]/40"
                />
                <div className="mt-2 flex items-center gap-2">
                  <button
                    onClick={confirmStatus}
                    disabled={statusBusy}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5"
                  >
                    {statusBusy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    Confirm
                  </button>
                  <button
                    onClick={() => { setPendingStatus(null); setStatusRemark(''); }}
                    className="text-xs font-medium text-slate-600 hover:text-slate-900 px-2 py-1.5"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Key info strip */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-4">
            {([
              [Phone, 'Phone', p.phone || '—'],
              [Mail, 'Email', p.email ? <a href={`mailto:${p.email}`} className="text-[#6366F1] hover:underline break-all">{p.email}</a> : '—'],
              [MapPin, 'Location', countryName || '—'],
              [UserIcon, 'Assigned to', p.assignedTo?.name || 'Unassigned'],
            ] as [any, string, React.ReactNode][]).map(([Icon, label, value]) => (
              <div key={label} className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Icon className="h-4 w-4 text-slate-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-slate-500">{label}</p>
                  <p className="text-sm font-semibold text-slate-800 truncate">{value}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-col items-start gap-y-1.5 sm:flex-row sm:flex-wrap sm:items-center gap-x-6 border-t border-slate-100 px-4 py-3 text-sm">
            <span className="text-slate-500">Created: <span className="font-medium text-slate-800">{formatDateTime(p.createdAt)}</span></span>
            <span className="text-slate-500">Last contact: <span className="font-medium text-slate-800">{p.lastContactAt ? timeAgo(p.lastContactAt) : '—'}</span></span>
            <span className="text-slate-500">Demo: <span className="font-medium text-slate-800">{p.demoScheduledAt ? formatDateTime(p.demoScheduledAt) : '—'}</span></span>
            {p.expectedCloseDate && (
              <span className="text-slate-500">Expected close: <span className="font-medium text-slate-800">{formatDate(p.expectedCloseDate)}</span></span>
            )}
            <button
              type="button"
              onClick={() => setTab('timeline')}
              className="sm:ml-auto inline-flex items-center gap-1 text-sm font-medium text-[#6366F1] hover:underline"
            >
              <Plus className="h-3.5 w-3.5" /> Add note
            </button>
          </div>
        </div>

        {/* Tabs + content in one card */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="no-scrollbar flex gap-1 overflow-x-auto border-b border-slate-100 px-2 sm:px-3 py-2.5">
            {([
              { key: 'details', label: 'Details', icon: Info, badge: 0 },
              { key: 'timeline', label: 'Timeline', icon: History, badge: activities.length },
              { key: 'demo', label: 'Demo', icon: CalendarIcon, badge: bookings.length },
            ] as const).map(t => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`group flex flex-shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-3 sm:px-4 py-2 text-sm font-medium transition-colors focus:outline-none ${
                    active ? 'bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white font-semibold shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <Icon className={`h-4 w-4 ${active ? 'text-white' : 'text-slate-400 group-hover:text-slate-600'}`} />
                  {t.label}
                  {t.badge > 0 && (
                    <span className={`min-w-[18px] rounded-full px-1.5 text-center text-[11px] font-semibold leading-[18px] ${active ? 'bg-white/25 text-white' : 'bg-slate-100 text-slate-600'}`}>{t.badge}</span>
                  )}
                </button>
              );
            })}
          </div>

        {/* Body */}
        <div className="p-3 sm:p-5 space-y-5">
          {tab === 'demo' ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-[#6366F1]" /> Demos
                    <span className="text-sm font-normal text-slate-400">({bookings.length})</span>
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">All demo bookings for this prospect — past + upcoming.</p>
                </div>
                <button
                  onClick={() => setShowBookDemo(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] px-3.5 py-2 text-sm font-semibold text-white shadow-sm"
                >
                  <CalendarIcon className="h-4 w-4" /> Book Demo
                </button>
              </div>
              {bookingsLoading ? (
                <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#6366F1]" /></div>
              ) : bookings.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 px-6 py-10 text-center">
                  <CalendarIcon className="h-9 w-9 mx-auto text-slate-300 mb-2" />
                  <p className="text-sm font-semibold text-slate-700">No demos yet</p>
                  <p className="text-xs text-slate-500 mt-1">Click Book Demo to schedule one.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {bookings.map(b => (
                    <DemoBookingCard key={b.id} booking={b} onChange={() => { loadBookings(); fetch(); onChange(); }} />
                  ))}
                </div>
              )}
            </div>
          ) : loading ? (
            <Loader2 className="h-5 w-5 animate-spin text-[#6366F1] mx-auto my-8" />
          ) : tab === 'timeline' ? (
            <>
              {/* Add note */}
              <div>
                <p className="text-xs text-slate-500 mb-2">Add to timeline</p>
                <div className="flex gap-2">
                  <textarea
                    value={noteText}
                    onChange={e => setNoteText(e.target.value)}
                    placeholder="What happened? E.g. 'Demo scheduled for Friday 4pm'"
                    rows={2}
                    className="flex-1 text-sm rounded-lg border border-slate-200 p-2 resize-none focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                  />
                  <button
                    disabled={!noteText.trim() || logBusy === 'note'}
                    onClick={() => log('note', noteText)}
                    className="self-stretch px-4 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] disabled:opacity-50 text-white text-sm font-semibold"
                  >
                    {logBusy === 'note' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
                  </button>
                </div>
              </div>

              {/* Timeline */}
              <div>
                <p className="text-xs text-slate-500 mb-2">Timeline ({activities.length})</p>
                {!activities.length ? (
                  <p className="text-xs text-slate-400 text-center py-4">No activity yet</p>
                ) : (
                  <div className="space-y-2">
                    {activities.map((a: any) => <TimelineEntry key={a.id} entry={a} />)}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Meta grid */}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <Meta label="Source" value={p.source?.replace('_', ' ') ?? '—'} />
                <Meta label="Estimated" value={p.estimatedValue ? `₹${p.estimatedValue.toLocaleString('en-IN')}` : '—'} />
                <Meta label="Expected close" value={p.expectedCloseDate ? formatDate(p.expectedCloseDate) : '—'} />
                <Meta label="Last contact" value={p.lastContactAt ? formatDateTime(p.lastContactAt) : '—'} />
                <Meta label="Demo scheduled" value={p.demoScheduledAt ? formatDateTime(p.demoScheduledAt) : '—'} />
                <Meta label="Assigned to" value={p.assignedTo?.name ?? 'Unassigned'} />
                {/* Campaign attribution for ad-sourced leads (Facebook etc.) */}
                {(prospect.campaignName || prospect.adName || prospect.formId || prospect.adId || prospect.campaignId || prospect.fbclid || prospect.leadgenId) && (
                  <>
                    {prospect.campaignName && <Meta label="Campaign" value={prospect.campaignName} />}
                    {prospect.adName && <Meta label="Ad" value={prospect.adName} />}
                    {prospect.formId && <Meta label="Form ID" value={prospect.formId} />}
                    {prospect.adId && <Meta label="Ad ID" value={prospect.adId} />}
                    {prospect.campaignId && <Meta label="Campaign ID" value={prospect.campaignId} />}
                    {prospect.leadgenId && <Meta label="Leadgen ID" value={prospect.leadgenId} />}
                    {prospect.fbclid && <Meta label="Click ID (fbclid)" value={prospect.fbclid} />}
                  </>
                )}
                {detail?.convertedTenant && (
                  <>
                    <Meta label="Converted to tenant" value={detail.convertedTenant.name} />
                    <Meta label="Converted on" value={detail.convertedAt ? formatDate(detail.convertedAt) : '—'} />
                  </>
                )}
                {p.lossReason && (
                  <div className="col-span-2 bg-red-50 border border-red-100 rounded p-2">
                    <p className="text-[10px] font-semibold uppercase text-red-600">Loss reason</p>
                    <p className="text-xs text-red-700">{p.lossReason}</p>
                  </div>
                )}
              </div>

              {p.notes && (
                <div>
                  <p className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Notes</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded p-3">{p.notes}</p>
                </div>
              )}

            </>
          )}
        </div>
        </div>
      </div>

      {showEdit && (
        <ProspectFormDrawer
          prospect={prospect}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); fetch(); onChange(); }}
        />
      )}
      {showConvert && (
        <ConvertModal
          prospect={prospect}
          onClose={() => setShowConvert(false)}
          onConverted={() => { setShowConvert(false); fetch(); onChange(); }}
        />
      )}
      {showBookDemo && (
        <BookDemoModal
          prospect={prospect}
          onClose={() => setShowBookDemo(false)}
          onBooked={() => { setShowBookDemo(false); fetch(); loadBookings(); onChange(); setTab('demo'); }}
        />
      )}
      <ConfirmModal
        isOpen={showDelete}
        title="Delete prospect?"
        message={`Permanently delete "${prospect.companyName}"? This cannot be undone.`}
        confirmText="Delete"
        onConfirm={remove}
        onCancel={() => setShowDelete(false)}
      />
    </div>
  );
}

// ── Convert modal ──────────────────────────────────────────────────────────

function ConvertModal({
  prospect,
  onClose,
  onConverted,
}: {
  prospect: Prospect;
  onClose: () => void;
  onConverted: () => void;
}) {
  const [plans, setPlans] = useState<{ id: string; name: string; price: number }[]>([]);
  const [planId, setPlanId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  const [result, setResult] = useState<any>(null);

  useEffect(() => {
    saApi.get('/super-admin/plans')
      .then(r => {
        const list = r.data?.plans ?? r.data ?? [];
        setPlans(list);
        // Default to Free.
        const free = list.find((p: any) => p.name === 'Free');
        if (free) setPlanId(free.id);
        else if (list.length > 0) setPlanId(list[0].id);
      })
      .catch(() => toast('error', 'Failed to load plans'))
      .finally(() => setLoading(false));
  }, []);

  const submit = async () => {
    setConverting(true);
    try {
      const { data } = await saApi.post(`/super-admin/prospects/${prospect.id}/convert`, { planId, autoMarkWon: true });
      setResult(data);
      toast('success', 'Tenant created — invitation link ready');
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Conversion failed'));
    } finally { setConverting(false); }
  };

  if (result) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Tenant created!</h2>
              <p className="text-xs text-slate-500">{result.tenant.name} on the {result.plan.name} plan</p>
            </div>
          </div>
          <p className="text-sm text-slate-600 mb-3">
            We've created the workspace. Send this password-set link to{' '}
            <strong>{result.adminUser.email}</strong> so they can log in:
          </p>
          <div className="bg-slate-900 rounded-lg p-3 flex items-center gap-2 mb-3">
            <code className="text-xs text-emerald-300 break-all flex-1">{result.passwordSetLink}</code>
            <button
              onClick={() => { navigator.clipboard.writeText(result.passwordSetLink); toast('success', 'Copied'); }}
              className="p-1.5 hover:bg-slate-700 rounded text-slate-300"
            >
              <Copy className="h-4 w-4" />
            </button>
          </div>
          <p className="text-xs text-slate-400 mb-5">
            Link is valid for 7 days. The tenant is ready to log in immediately.
          </p>
          <div className="flex justify-end">
            <button
              onClick={() => { setResult(null); onConverted(); }}
              className="px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-1">Convert to Tenant</h2>
        <p className="text-sm text-slate-500 mb-4">
          Create a real BrixLead tenant for <strong>{prospect.companyName}</strong> with{' '}
          <strong>{prospect.contactName}</strong> as the admin.
        </p>

        <div className="bg-slate-50 rounded-lg p-3 mb-4 text-xs space-y-1">
          <div><span className="text-slate-500">Company:</span> <span className="font-semibold">{prospect.companyName}</span></div>
          <div><span className="text-slate-500">Admin:</span> <span className="font-semibold">{prospect.contactName} ({prospect.email})</span></div>
        </div>

        <label className="block text-sm font-normal text-slate-700 mb-2">Choose plan</label>
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-[#6366F1]" />
        ) : (
          <SelectField
            value={planId}
            onChange={e => setPlanId(e.target.value)}
            className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm mb-4"
          >
            {plans.map(p => (
              <option key={p.id} value={p.id}>
                {p.name} {p.price > 0 ? `— ₹${p.price.toLocaleString('en-IN')}/mo` : '(Free)'}
              </option>
            ))}
          </SelectField>
        )}

        <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-xs text-amber-800 mb-5 flex gap-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <p>The prospect's status will be marked <strong>Won</strong>. A password-set link will be generated for the admin to log in for the first time.</p>
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm">
            Cancel
          </button>
          <button onClick={submit} disabled={converting || !planId}
            className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold inline-flex items-center gap-2">
            {converting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Create tenant
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tiny presentation helpers ──────────────────────────────────────────────

const INPUT_CLS = 'w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]';

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-600 mb-1 block">{label}</span>
      {children}
    </label>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase text-slate-500">{label}</p>
      <p className="text-sm text-slate-800 capitalize">{value}</p>
    </div>
  );
}

function TimelineEntry({ entry }: { entry: any }) {
  const { actionType, payload, createdAt, actorName } = entry;
  const ICON: Record<string, any> = {
    created: Plus,
    note_added: FileText,
    call_made: Phone,
    email_sent: Mail,
    whatsapp_sent: MessageCircle,
    demo_scheduled: CalendarIcon,
    demo_completed: CheckCircle,
    proposal_sent: FileText,
    status_changed: RefreshCw,
    field_updated: Pencil,
    assigned: UserIcon,
    converted_to_tenant: ArrowRight,
  };
  const Icon = ICON[actionType] ?? FileText;

  let summary: React.ReactNode = actionType.replace(/_/g, ' ');
  if (actionType === 'status_changed') {
    summary = <>Status changed from <b>{statusLabel(payload.from)}</b> to <b>{statusLabel(payload.to)}</b>{payload.note ? <> — {payload.note}</> : null}</>;
  } else if (actionType === 'note_added') {
    summary = <>Note: <span className="text-slate-700">{payload.body}</span></>;
  } else if (actionType === 'call_made') {
    summary = <>Logged a call{payload.summary ? `: ${payload.summary}` : ''}</>;
  } else if (actionType === 'email_sent') {
    summary = <>Logged an email{payload.summary ? `: ${payload.summary}` : ''}</>;
  } else if (actionType === 'whatsapp_sent') {
    summary = <>Logged a WhatsApp message{payload.summary ? `: ${payload.summary}` : ''}</>;
  } else if (actionType === 'converted_to_tenant') {
    summary = <>Converted to tenant <b>{payload.tenantName}</b> on the <b>{payload.planName}</b> plan</>;
  } else if (actionType === 'created') {
    summary = <>Prospect created from <b>{payload.source ?? 'unknown'}</b></>;
  } else if (actionType === 'field_updated') {
    summary = <>Updated: {payload.fields?.join(', ')}</>;
  }

  return (
    <div className="flex gap-3 items-start">
      <div className="h-7 w-7 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
        <Icon className="h-3.5 w-3.5 text-[#6366F1]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-700 leading-relaxed">{summary}</p>
        <p className="text-[10px] text-slate-400 mt-0.5">
          {actorName ?? 'System'} · {formatDateTime(createdAt)}
        </p>
      </div>
    </div>
  );
}

// ── Book Demo modal (internal booking from a prospect) ──────────────────────

function BookDemoModal({ prospect, onClose, onBooked }: { prospect: Prospect; onClose: () => void; onBooked: () => void }) {
  const [cfg, setCfg] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [slots, setSlots] = useState<string[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [booking, setBooking] = useState(false);
  const [done, setDone] = useState<any>(null);

  useEffect(() => {
    saApi.get(`/super-admin/prospects/${prospect.id}/demo-config`)
      .then(({ data }) => setCfg(data))
      .catch(() => setCfg({ active: false }))
      .finally(() => setLoading(false));
  }, [prospect.id]);

  useEffect(() => {
    if (!date || !cfg?.active) return;
    setSlotsLoading(true);
    saApi.get(`/super-admin/prospects/${prospect.id}/demo-slots`, { params: { date } })
      .then(({ data }) => setSlots(Array.isArray(data) ? data : []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [date, cfg, prospect.id]);

  const tz = cfg?.timezone || 'UTC';
  const fmtTime = (iso: string) => formatTime(iso, tz);

  const book = async (start: string) => {
    setBooking(true);
    try {
      const { data } = await saApi.post(`/super-admin/prospects/${prospect.id}/book-demo`, { start });
      setDone(data);
      toast('success', 'Demo booked');
      onBooked();
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Failed to book demo'));
      setBooking(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-slate-900">Book a demo — {prospect.contactName}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="h-5 w-5 text-slate-500" /></button>
        </div>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#6366F1]" /></div>
        ) : done ? (
          <div className="text-center py-4">
            <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3"><CheckCircle className="h-6 w-6 text-emerald-600" /></div>
            <p className="text-sm font-semibold text-slate-800">Demo booked!</p>
            <p className="text-xs text-slate-500 mt-1">
              {formatDateTime(done.startAt, tz)} ({tz})
            </p>
            {done.meetingLink && (
              <a href={done.meetingLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-4 px-4 h-9 rounded-lg bg-[#6366F1] text-white text-xs font-semibold">
                <ExternalLink className="h-3.5 w-3.5" /> Join Zoom
              </a>
            )}
            <div className="mt-4"><button onClick={onClose} className="text-sm text-slate-500 hover:text-slate-700">Close</button></div>
          </div>
        ) : !cfg?.active ? (
          <p className="text-sm text-slate-500 text-center py-8">The advisor hasn't set up demo availability yet.</p>
        ) : (
          <>
            <p className="text-xs text-slate-500 mb-3">{cfg.eventName} · {cfg.duration} min · Zoom · <span className="text-slate-400">times in {tz}</span></p>
            <div className="mb-3"><DateInput value={date} onChange={setDate} className="w-full" /></div>
            {slotsLoading ? (
              <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#6366F1]" /></div>
            ) : slots.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">No open slots — try another date.</p>
            ) : (
              <div className="grid grid-cols-3 gap-2 max-h-[40vh] overflow-y-auto">
                {slots.map(s => (
                  <button key={s} disabled={booking} onClick={() => book(s)} className="px-2 h-10 rounded-lg border border-slate-200 hover:border-[#6366F1] hover:bg-indigo-50 disabled:opacity-50 text-sm font-medium text-slate-700">
                    {fmtTime(s)}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
